import moment from 'moment';
import { DisplayDecorator } from '../decorators/display-decorator';
import { DropdownModel } from '../models/complex/dropdown.model';
import { RadioModel } from '../models/complex/radio.model';
import { DateModel } from '../models/primitive/date.model';
import { MultilineTextModel } from '../models/primitive/multiline-text.model';
import { TextModel } from '../models/primitive/text.model';
import { BooleanModel } from '../models/primitive/boolean.model';
import { CompositeModel } from '../models/complex/composite.model';
import { isDefined } from './is-defined';
import { NumberModel } from '../models/primitive/number.model';

/**
 * Resets a given odata model in the correct way to wipe out internal values.
 * Implementation does not cover all existing odata model classes and might need to be extended for additional
 * use-cases; only what was required for the confirmation complaints section is done for now.
 *
 * @param m the odata model to reset
 * @throws if the OData model subtype is not supported yet, an error is thrown
 */
export function resetODataModel(m: unknown): void {
    if (m instanceof TextModel) {
        m.value = '';
    } else if (m instanceof NumberModel) {
        m.value = undefined;
    } else if (m instanceof MultilineTextModel) {
        m.value = '';
    } else if (m instanceof DropdownModel) {
        m.deselect();
    } else if (m instanceof DateModel) {
        m.value = moment.invalid();
    } else if (m instanceof RadioModel) {
        m.selectByIndex(-1);
    } else if (m instanceof DisplayDecorator) {
        resetODataModel(m.model);
    } else if (m instanceof BooleanModel) {
        m.value = false;
    } else {
        throw new Error(`resetODataModel: unknown model type '${typeof m}'`);
    }
}

function isKey<T extends object>(x: T, k: PropertyKey): k is keyof T {
    return k in x;
}

/**
 * Retrieves all properties from a composite model and runs resetODataModel on all of them
 * that implement the ODataModel interface, ie. have a 'name' and 'value' property
 *
 * @param compositeModel the composite model that we want to reset
 */
export function resetCompositeModel(compositeModel: CompositeModel): void {
    Object.keys(compositeModel).forEach((k) => {
        if (isKey(compositeModel, k)) {
            const child = compositeModel[k];
            if (
                isDefined(child) &&
                typeof child === 'object' &&
                child.hasOwnProperty('name') &&
                child.hasOwnProperty('value')
            ) {
                resetODataModel(child);
            }
        }
    });
}
